import { FC, useEffect, useState } from 'react';
import { RecordKey, RecordValueWithSensograms, SENSOR_NATURE, getFullRecord, getFullRecords, putModel } from '../../../services/cache/idb';
import Plot from 'react-plotly.js';
import { FlexCol, Paper } from '../../common/common';
import { computeSignature, DEFAULT_SIGNATURE_PROCESSING_PARAMS, handleNormalisation } from '../../analysis/signature';
import { getPcaFigure, getSignaturesFigure } from '../../analysis/figures';
import { getColormap } from '../../analysis/colormap';
import { performAnalysis } from '../../analysis/pca';
import { DEFAULT_PLOTLY_MARGIN } from '../../../utils/constants/constants';
import { Button, Col, InputNumber, Row, Tooltip, message as antdMessage, Input, Typography, Alert, message } from 'antd';
import { optimizeEllipsesSigma, scaleEllipsesSigma } from '../../analysis/ellipse';
import { RedoOutlined, SaveOutlined } from '@ant-design/icons';
import { useMediaQuery } from '../../../utils/helpers/utils';
import { ModelingWidget } from '../modelingWidget';
import { mean } from '../../analysis/utils';
import { ColormapWidget } from '../Colormap/colormapWidget';
import { GenericModelV2, EllipsesData, Metadata, ModelCategory, ProcessingPipelineParams, PipelineType } from '../../analysis/definitions';
import { WoundKineticDisplay } from '../WoundKineticDisplay';
import { SignatureWithSpotgrid } from '../../../types/types';
import { computeDeltaSensor } from '../../analysis/compute';
// import { intensityScalingFactor } from '../../../pages/SensePage/Recording/OdorResultsModal';

const generateEllipsesModel = (spotgrid1d: number[], sigmaX: number, sigmaY: number, pcaEigenvectors: number[][], groupedScaledEllipses: Record<string, number[]>, cmap: Record<string, [number, number, number]>, name: string): GenericModelV2 => {
  const metadata: Metadata = {
    ID: name,
    created_at: Date(),
    spotfile: spotgrid1d,
    type: ModelCategory.Ellipses,
    debug: name.startsWith('DEBUG'),
  };

  const data: EllipsesData = {
    type: ModelCategory.Ellipses,
    groupedScaledEllipses,
    pcaEigenvectors,
    cmap,
    sigmaX,
    sigmaY,
  };

  return {
    metadata,
    data,
  };
};

export const RecordsAnalysisWidget: FC<{
  recordKeys: RecordKey[];
  recordNames: string[]; // used to trigger re-render
}> = ({ recordKeys, recordNames }) => {
  const [records, setRecords] = useState<RecordValueWithSensograms[]>([]);
  const [signatures, setSignatures] = useState<number[][]>([]);
  const [intensities, setIntensities] = useState<number[]>([]);
  const [commonSpotsgrid, setCommonSpotsgrid] = useState<number[]>([]);

  const [signaturesPlotlyData, setSignaturesPlotlyData] = useState<Plotly.Data[]>([]);
  const [signaturesPlotlyLayout, setSignaturesPlotlyLayout] = useState<Partial<Plotly.Layout>>({});

  const [pcaPlotlyData, setPcaPlotlyData] = useState<Plotly.Data[]>([]);
  const [pcaPlotlyLayout, setPcaPlotlyLayout] = useState<Partial<Plotly.Layout>>({});

  const [pcaEigenvectors, setPcaEigenvectors] = useState<number[][]>([]);
  const [groupedScaledEllipses, setGroupedScaledEllipses] = useState<Record<string, number[]>>({});

  const [sigmaMultiplierRx, setSigmaMultiplierRx] = useState<number | null>(null);
  const [sigmaMultiplierRy, setSigmaMultiplierRy] = useState<number | null>(null);

  const [analysis, setAnalysis] = useState<{
    pcaExplainedVarianceRatio: number[];
    groupedProjections: Record<string, number[][]>;
    groupedEllipses: Record<string, number[]>;
    groupedScaledEllipses: Record<string, number[]>;
    pcaEigenvectors: number[][];
  } | null>(null);
  const [cmap, setCmap] = useState<Record<string, [number, number, number]>>({});
  const [modelName, setModelName] = useState<string>('');
  const isMobile = useMediaQuery();

  // wound analysis display
  const [recordName, setRecordName] = useState<string | null>(null);
  const [recordIntensities, setRecordIntensities] = useState<{ timestamp: number; intensity: number }[]>([]);

  const getRecordIntensity = (record: RecordValueWithSensograms, humidityCalibrant: SignatureWithSpotgrid): number => {
    const baselineBounds = [record.baselineStart, record.baselineEnd];
    const analyteBounds = [record.analyteStart, record.analyteEnd];
    let baselineHumidity = mean(record.humiditySeries.slice(...baselineBounds));
    let analyteHumidity = mean(record.humiditySeries.slice(...analyteBounds));
    let deltaH = analyteHumidity - baselineHumidity;

    let { normalisation: normParam, ...processingParamNoNorm } = DEFAULT_SIGNATURE_PROCESSING_PARAMS;

    let finalProcessingParameters: ProcessingPipelineParams = {
      pipelineType: PipelineType.Classic,
      aggregation: processingParamNoNorm.aggregation,
    };

    if (humidityCalibrant) {
      console.log('humidityCalibrant', humidityCalibrant);
      // fetch delta H of the record
      finalProcessingParameters.humidityCorrectionParams = { deltaH: deltaH, calibrant: humidityCalibrant };
    }
    // console.log('finalProcessingparametrs', finalProcessingParameters);
    let [_correctedSignature] = computeSignature(record, finalProcessingParameters);
    console.log('corrected signature', _correctedSignature);
    let intensityScalingFactor = record.intensityScalingFactor || 1;
    return intensityScalingFactor * Math.max(..._correctedSignature);
  };

  // check that names are identical
  const allNamesIdentical = recordNames.length > 0 && recordNames.every((name) => name === recordNames[0]);

  useEffect(() => {
    let cancelled = false;
    console.log('fetching selected records...');
    getFullRecords(recordKeys).then(async (_records) => {
      if (cancelled) return;

      const fetchedNames = _records.map((record) => record.name);
      const allNamesIdentical = fetchedNames.length > 0 && fetchedNames.every((name) => name !== undefined && name === fetchedNames[0]);
      if (!allNamesIdentical || fetchedNames[0] === undefined) {
        message.warning('Fetched records must all have identical names.');
        setRecordName(null);
        return;
      }

      setRecordName(fetchedNames[0]); // for name display
      _records.sort((a, b) => b.absoluteTimestamp - a.absoluteTimestamp);
      setRecords(_records);
      // console.log('building humidity calibrant look up table...');
      let humidityCalibrantLookUpTable: Record<string, SignatureWithSpotgrid> = {};
      // build a look up table linking humidity calibrant and water affinities
      let { normalisation: norm, ...processingParamNoNorm } = DEFAULT_SIGNATURE_PROCESSING_PARAMS;
      for (let i = 0; i < _records.length; i++) {
        // console.log('i is', i);
        let humidityCalibrantInForLoop = _records[i].humidityCalibrantRefKey;
        if (humidityCalibrantInForLoop !== undefined && humidityCalibrantInForLoop in humidityCalibrantLookUpTable) {
          continue;
        } else if (humidityCalibrantInForLoop !== undefined) {
          console.log('fetching humidity record');
          let waterRecord = await getFullRecord(humidityCalibrantInForLoop);
          let [_signature, _spotsgrid1d] = computeSignature(waterRecord, processingParamNoNorm);
          let deltaHumidity = computeDeltaSensor(waterRecord, SENSOR_NATURE.Humidity);
          let deltaTemperature = computeDeltaSensor(waterRecord, SENSOR_NATURE.Temperature);
          // fill table
          humidityCalibrantLookUpTable[humidityCalibrantInForLoop] = {
            recordKey: humidityCalibrantInForLoop,
            signature: _signature,
            spotsgrid: _spotsgrid1d,
            deltaHumidity: deltaHumidity,
            deltaTemperature: deltaTemperature,
          };
        }
      }
      // console.log('humidityCalibrantLookUpTable', humidityCalibrantLookUpTable);

      const recordIntensities = _records.map((record) => ({
        timestamp: record.absoluteTimestamp,
        intensity: record.humidityCalibrantRefKey ? getRecordIntensity(record, humidityCalibrantLookUpTable[record.humidityCalibrantRefKey]) : 0,
      }));

      setRecordIntensities(recordIntensities);
      console.log('recordIntensities', recordIntensities);
    });

    return () => {
      cancelled = true;
    };
  }, [recordKeys]);

  // calcul intensities and signatures for passfail
  useEffect(() => {
    const f = () => {
      if (records.length === 0) {
        return;
      }
      let signatures: number[][] = [];
      let intensities: number[] = [];
      let spotsgrids1d: number[][] = [];

      let { normalisation: normParam, ...processingParamNoNorm } = DEFAULT_SIGNATURE_PROCESSING_PARAMS;
      // aggregate but do not normalise as we need to compute intensity on signature
      for (let record of records) {
        let [_signature, _spotsgrid1d] = computeSignature(record, processingParamNoNorm);

        if (normParam !== undefined) {
          let normalizedSignature = handleNormalisation(_signature, _spotsgrid1d, normParam);
          signatures.push(normalizedSignature);
        } else {
          signatures.push(_signature);
        }

        spotsgrids1d.push(_spotsgrid1d);
        intensities.push(mean(_signature));
      }
      const signatureLength = signatures[0].length;
      for (let sign of signatures) {
        if (sign.length !== signatureLength) {
          throw new Error('signatures have different lengths');
        }
      }
      // check if all spotsgrids are the same
      const commonSpotsgrid = spotsgrids1d[0];
      for (let spotsgrid1d of spotsgrids1d) {
        if (spotsgrid1d.length !== commonSpotsgrid.length) {
          throw new Error('spotsgrids have different lengths');
        }
        for (let i = 0; i < spotsgrid1d.length; i++) {
          if (spotsgrid1d[i] !== commonSpotsgrid[i]) {
            throw new Error('spotsgrids are not the same');
          }
        }
      }
      console.log('common spotsgrid:', commonSpotsgrid);
      console.log('computed signatures:', signatures);
      console.log('computed intensities:', intensities);

      setCommonSpotsgrid(commonSpotsgrid);
      setSignatures(signatures);
      setIntensities(intensities);
    };

    try {
      f();
    } catch (e: any) {
      console.error(e);
      antdMessage.error(`Error while computing signatures: ${e.message}`);
    }
  }, [records]);

  // calcul graph
  useEffect(() => {
    if (signatures.length === 0) {
      return;
    }

    let labels = records.map((record) => (record.name ? record.name : 'unnamed'));
    let uniqueLabels = Array.from(new Set(labels));
    let cmap = getColormap(uniqueLabels);

    let analysis = performAnalysis(signatures, labels);

    setAnalysis(analysis);
    setCmap(cmap);
  }, [signatures]);

  useEffect(() => {
    if (analysis === null) return;

    console.log('analysis widget: sigmaX', sigmaMultiplierRx, 'sigmaY', sigmaMultiplierRy);
    if (sigmaMultiplierRx === null || sigmaMultiplierRy === null) {
      let [groupedScaledEllipses, _sigmaRx, _sigmaRy] = optimizeEllipsesSigma(signatures, analysis.groupedScaledEllipses);
      if (sigmaMultiplierRx === null) {
        setSigmaMultiplierRx(_sigmaRx);
      }
      if (sigmaMultiplierRy === null) {
        setSigmaMultiplierRy(_sigmaRy);
      }
    }
    setAnalysis(analysis);
  }, [analysis, sigmaMultiplierRx, sigmaMultiplierRy]);

  useEffect(() => {
    if (signatures.length === 0) return;
    if (analysis === null) return;

    let labels = records.map((record) => (record.name ? record.name : 'unnamed'));
    let uniqueLabels = Array.from(new Set(labels));
    let cmap = getColormap(uniqueLabels);

    if (sigmaMultiplierRx !== null && sigmaMultiplierRy !== null) {
      analysis.groupedScaledEllipses = scaleEllipsesSigma(analysis.groupedEllipses, sigmaMultiplierRx, sigmaMultiplierRy);
    }

    let { data: signaturesPlotlyData, layout: signaturesPlotlyLayout } = getSignaturesFigure(commonSpotsgrid, signatures, labels, cmap);

    let { data: pcaPlotlyData, layout: pcaPlotlyLayout } = getPcaFigure(cmap, analysis.pcaExplainedVarianceRatio, analysis.groupedProjections, analysis.groupedScaledEllipses);

    signaturesPlotlyLayout.margin = DEFAULT_PLOTLY_MARGIN;
    pcaPlotlyLayout.margin = DEFAULT_PLOTLY_MARGIN;

    signaturesPlotlyLayout.showlegend = false;
    pcaPlotlyLayout.showlegend = false;

    setSignaturesPlotlyData(signaturesPlotlyData);
    setSignaturesPlotlyLayout(signaturesPlotlyLayout);

    setPcaPlotlyData(pcaPlotlyData);
    setPcaPlotlyLayout(pcaPlotlyLayout);

    setPcaEigenvectors(analysis.pcaEigenvectors);
    setGroupedScaledEllipses(analysis.groupedScaledEllipses);
  }, [analysis, sigmaMultiplierRx, sigmaMultiplierRy]);

  const handleSaveModel = () => {
    if (sigmaMultiplierRx !== null && sigmaMultiplierRy !== null && modelName !== '' && commonSpotsgrid.length > 0) {
      const model = generateEllipsesModel(commonSpotsgrid, sigmaMultiplierRx, sigmaMultiplierRy, pcaEigenvectors, groupedScaledEllipses, cmap, modelName);
      putModel(model);
      antdMessage.success('Model saved!');
    } else {
      antdMessage.error("Couldn't save model. Please ensure all parameters are set and model name is provided.");
    }
  };

  if (records.length === 0) return null;

  return <FlexCol>{recordName !== null && <WoundKineticDisplay patientId={recordName} recordListWithOdorIntensity={recordIntensities} />}</FlexCol>;
};
